import { FeatureToggleKey } from "@omni/types/enums/featureToggleKey.enum";
import { attempt } from "@omni/utils/function.utils";
import { isDefined } from "@omni/utils/helper.utils";
import * as SessionStorage from "@omni/utils/sessionStorage.utils";

import FeatureTogglesService from "@/services/featureToggle/featureToggles.service";
import { StorageKeys } from "@/utils/enums";

const DomainWhitelist = ["*.co"];
const neverbounceValidationPromises = new Map<string, Promise<boolean>>();

const isAllowed = (origin: string, pattern: string) => {
    if (pattern.startsWith("*.")) {
        return origin?.endsWith(pattern.slice(2));
    }

    const regexp = new RegExp(
        `^${pattern.replace(/\./g, "\\.").replace(/\*/g, ".*?")}$`
    );

    return regexp.test(origin);
};

const getCache = () => {
    const cache = SessionStorage.getItem(
        StorageKeys.NeverbounceValidatedEmails
    );

    if (!cache) {
        return new Map<string, boolean>();
    } else {
        return attempt(
            () => new Map<string, boolean>(JSON.parse(cache)),
            new Map<string, boolean>()
        );
    }
};

const cache = (email: string, valid: boolean) => {
    const cache = getCache();

    const key = email.toLowerCase();

    cache.set(key, valid);

    SessionStorage.setItem(
        StorageKeys.NeverbounceValidatedEmails,
        JSON.stringify([...cache])
    );
};

const getCached = (email: string) => {
    const cache = getCache();

    const key = email.toLowerCase();

    return cache.get(key);
};

const validateEmail = (email: string) => {
    const key = email.toLowerCase();

    const pendingValidation = neverbounceValidationPromises.get(key);

    if (pendingValidation) {
        return pendingValidation;
    } else {
        const newValidation = new Promise<boolean>((resolve, reject) => {
            if (!window._nb) {
                return resolve(true);
            }

            window._nb.api.getValidatePublic(
                email,
                (responseData: any) => {
                    const isInvalid =
                        responseData.response.result === "invalid";

                    cache(email, !isInvalid);

                    resolve(!isInvalid);
                },
                () => {
                    reject();
                }
            );
        }).finally(() => {
            neverbounceValidationPromises.delete(key);
        });

        neverbounceValidationPromises.set(key, newValidation);

        return newValidation;
    }
};

const validateEmailAddress = async (email: string) => {
    if (
        !FeatureTogglesService.isEnabled(FeatureToggleKey.FEATURE_NEVERBOUNCE)
    ) {
        return true;
    }

    const cached = getCached(email);
    if (isDefined(cached)) {
        return cached;
    }

    if (DomainWhitelist.some((p) => isAllowed(email, p))) {
        return false;
    }

    return validateEmail(email);
};

export { validateEmailAddress };
